/**
 * Shared interfaces, types, and default configurations for the SensitiveFileInput feature.
 *
 * This file serves as the platform-agnostic contract consumed by both the
 * web and mobile component implementations.
 */

import { IFormFieldProps } from '../form/form-field-props'

export const AUTO_HIDE_SECONDS = 30
export interface IProtectedViewConfig {
  title?: string // e.g., "Protected Information"
  subtitle?: string // e.g., "This image contains PII. Access is logged..."
  submitText?: string // e.g., "View Document"
}

export interface IAccessReasonConfig {
  title?: string // e.g., "Access Reason Required"
  text?: string // e.g., "Please provide a reason for viewing..."
  placeholder?: string // e.g., "E.g., Verifying identity..."
  confirmText?: string // e.g., "Submit & View"
}

export interface IAccessDeniedConfig {
  title?: string // e.g., "Access Failed"
  retryText?: string // e.g., "Retry"
  cancelText?: string // e.g., "Cancel"
}

// Extends the shared IFormFieldProps so all ui:form:sensitiveFileField
// implementations (NM / DW / MW) accept the standard field surface —
// `isReadOnly`, `isDisabled`, `requiredType`, `tooltip`, `inlineHelpText`,
// `errors`, `showPrefilledIndicator`, etc. — instead of a
// component-specific prop shape.
export interface ISensitiveFileInputProps extends IFormFieldProps<any> {
  emptyText?: string
  // Storage provider identifier passed through to
  // `requestSensitiveFileUploadCredentials`. Defaults to 'uploadcare'.
  provider?: string
  // Retention window applied to newly uploaded files. Defaults to '1y'.
  retentionAge?: string
  // Button copy for the empty-state upload affordance.
  buttonText?: string
  // Copy shown next to the eye icon when the field has a value.
  successText?: string

  protectedCfg?: IProtectedViewConfig
  reasonCfg?: IAccessReasonConfig
  deniedCfg?: IAccessDeniedConfig

  // Whether the current user is allowed to open the reason-gated viewer.
  // When false, a field with a saved value still renders the success
  // affordance (check icon + successText) but is not tappable and never
  // launches the request-access flow. Defaults to true for backwards
  // compatibility.
  canRequestAccess?: boolean
}

export const defaultProtectedConfig: IProtectedViewConfig = {
  title: 'Protected Information',
  subtitle: 'This image contains PII. Access is logged and restricted.',
  submitText: 'View Document',
}

export const defaultReasonConfig: IAccessReasonConfig = {
  title: 'Access Reason Required',
  text: 'Please provide a reason for viewing this driver’s license. This action will be logged.',
  placeholder: 'E.g., Verifying driver identity for shipment #12345',
  confirmText: 'Submit & View',
}

export const defaultDeniedConfig: IAccessDeniedConfig = {
  title: 'Access Failed',
  retryText: 'Retry',
  cancelText: 'Cancel',
}
